import { useCallback, useState } from 'react';
import type { Form, Forms } from '@atlassian/proforma-form-system/add-form';
import { fg } from '@atlassian/jira-feature-gating';
import { useIssueViewFormApi } from '../../common/api-provider/index.tsx';
import type { AvailableForm, AvailableForms, State } from './types.tsx';

export const useGetAvailableFormsService = (issueId: number) => {
	const [state, setState] = useState<State>({
		data: undefined,
		loading: false,
		error: undefined,
	});

	const issueViewFormApi = useIssueViewFormApi();
	const oldRequest = useCallback(
		() =>
			// issueViewFormApi.get will be replaced with a 'fetch' utility hook in the future which replaces ApiUtil
			issueViewFormApi.get<Forms>(`/api/1/issues/${issueId}/availableforms`),
		[issueId, issueViewFormApi],
	);

	const request = useCallback(
		() =>
			// issueViewFormApi.get will be replaced with a 'fetch' utility hook in the future which replaces ApiUtil
			issueViewFormApi
				.get<AvailableForms>(`/api/frontend/jira/issue/${issueId}/availableforms`)
				.then<Forms>((forms) => ({
					recommended: forms.recommended.map(toLegacyAvailableForm),
					available: forms.available.map(toLegacyAvailableForm),
				})),
		[issueId, issueViewFormApi],
	);

	const fetchAvailableForms = useCallback(async () => {
		try {
			setState({
				data: undefined,
				loading: true,
				error: undefined,
			});

			const data = await (fg('granular_permissions_on_forms_m1') ? request() : oldRequest());
			setState({
				data,
				loading: false,
				error: undefined,
			});

			return data;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			setState({
				data: undefined,
				loading: false,
				error,
			});

			return Promise.reject(error);
		}
	}, [request, oldRequest]);

	return { ...state, fetchAvailableForms };
};

const toLegacyAvailableForm = (form: AvailableForm): Form => {
	return {
		label: form.name,
		value: form.id,
		restrictionStatus: form.restrictionStatus,
	};
};
